import { Link } from 'react-router-dom';
import { useState } from 'react';

const footerLinks = {
  institucion: {
    title: 'Institución',
    links: [
      { label: 'Sobre SONIEM', href: '/nosotros' },
      { label: 'Autorización Indecopi', href: '/nosotros' },
      { label: 'Convenios FILAIE', href: '/nosotros' },
      { label: 'Transparencia', href: '/nosotros' },
      { label: 'Sede San Miguel', href: '/contacto' },
    ],
  },
  servicios: {
    title: 'Servicios',
    links: [
      { label: 'Gestión de Regalías', href: '/servicios' },
      { label: 'Estudio de Grabación', href: '/servicios' },
      { label: 'Licencias Musicales', href: '/servicios' },
      { label: 'Asesoría Legal', href: '/servicios' },
      { label: 'Capacitación', href: '/servicios' },
    ],
  },
  artistas: {
    title: 'Artistas',
    links: [
      { label: 'Directorio', href: '/artistas' },
      { label: 'Cómo Afiliarse', href: '/contacto' },
      { label: 'Beneficios', href: '/servicios' },
      { label: 'Preguntas Frecuentes', href: '/faq' },
      { label: 'Calendario de Pagos', href: '/servicios' },
    ],
  },
};

const socialLinks = [
  { icon: 'ri-facebook-fill', label: 'Facebook', href: 'https://facebook.com/SoniemPeru' },
  { icon: 'ri-instagram-line', label: 'Instagram', href: 'https://instagram.com/soniemperu' },
  { icon: 'ri-youtube-fill', label: 'YouTube', href: 'https://youtube.com/@SoniemPeru' },
];

export default function Footer() {
  const [email, setEmail] = useState('');
  const [subscribeStatus, setSubscribeStatus] = useState<'idle' | 'success' | 'error'>('idle');

  const handleSubscribe = (e: React.FormEvent) => {
    e.preventDefault();
    if (!email.trim()) return;
    setSubscribeStatus('success');
    setEmail('');
    setTimeout(() => setSubscribeStatus('idle'), 3000);
  };

  return (
    <footer className="relative bg-foreground-950 rounded-t-[2.5rem] md:rounded-t-[4rem] mt-8 mx-3 md:mx-5 overflow-hidden">
      <div className="px-6 md:px-10 lg:px-16 pt-16 md:pt-20 pb-10">
        {/* Top Section */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-10 mb-14">
          {/* Brand Column */}
          <div className="lg:col-span-2">
            <Link to="/" className="inline-block mb-5">
              <img
                src="https://storage.helloreaddy.io/project_files/b36b426b-b2cb-46b2-8bd1-3f029dd7b0e4/76aa933d-c1ed-427f-bd19-3cb55ca4b785_compressed_logo_header_soniem_b-3.webp"
                alt="SONIEM"
                className="h-10 md:h-12 w-auto"
              />
            </Link>
            <p className="text-secondary-400 text-sm leading-relaxed max-w-sm mb-6">
              Sociedad Nacional de Intérpretes y Ejecutantes de la Música.
              Protegiendo los derechos de los artistas musicales del Perú desde 2011.
            </p>

            {/* Newsletter */}
            <div>
              <p className="text-white text-sm font-semibold mb-3">
                Suscríbete a nuestro boletín
              </p>
              <form onSubmit={handleSubscribe} className="flex gap-0 max-w-sm">
                <input
                  type="email"
                  name="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="tu@email.com"
                  required
                  className="flex-1 px-4 py-3 bg-secondary-800/50 border border-secondary-700/50 rounded-l-full text-white text-sm placeholder:text-secondary-400 outline-none focus:border-primary-500/50 transition-colors"
                />
                <button
                  type="submit"
                  className="px-5 py-3 bg-primary-500 hover:bg-primary-600 text-white rounded-r-full transition-colors whitespace-nowrap cursor-pointer"
                >
                  <i className="ri-send-plane-fill" />
                </button>
              </form>
              {subscribeStatus === 'success' && (
                <p className="text-primary-400 text-xs mt-2">¡Gracias por suscribirte!</p>
              )}
            </div>
          </div>

          {/* Link Columns */}
          {Object.values(footerLinks).map((col) => (
            <div key={col.title}>
              <h4 className="text-white text-sm font-semibold mb-4 whitespace-nowrap">
                {col.title}
              </h4>
              <ul className="space-y-3">
                {col.links.map((link) => (
                  <li key={link.label}>
                    <Link
                      to={link.href}
                      className="text-secondary-400 hover:text-white text-sm transition-colors whitespace-nowrap"
                    >
                      {link.label}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>

        {/* Divider */}
        <div className="border-t border-secondary-800/50 pt-8">
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-6">
            {/* Contact Info */}
            <div className="flex flex-wrap items-center gap-4 md:gap-6">
              <span className="text-secondary-400 text-xs md:text-sm flex items-center gap-2">
                <i className="ri-map-pin-line text-primary-500" />
                San Miguel, Lima - Perú
              </span>
              <span className="text-secondary-400 text-xs md:text-sm flex items-center gap-2">
                <i className="ri-phone-line text-primary-500" />
                +51 983 151 655
              </span>
              <a
                href="mailto:soniem@soniemperu.com"
                className="text-secondary-400 hover:text-white text-xs md:text-sm flex items-center gap-2 transition-colors"
              >
                <i className="ri-mail-line text-primary-500" />
                soniem@soniemperu.com
              </a>
            </div>

            {/* Social */}
            <div className="flex items-center gap-3">
              {socialLinks.map((social) => (
                <a
                  key={social.label}
                  href={social.href}
                  target="_blank"
                  rel="noopener noreferrer nofollow"
                  className="w-9 h-9 rounded-full bg-secondary-800/50 flex items-center justify-center text-secondary-400 hover:text-white hover:bg-primary-500 transition-all duration-300 cursor-pointer"
                  aria-label={social.label}
                >
                  <i className={`${social.icon} text-sm`} />
                </a>
              ))}
            </div>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="border-t border-secondary-800/50 mt-8 pt-6 flex flex-col sm:flex-row items-center justify-between gap-4">
          <p className="text-secondary-500 text-xs text-center sm:text-left">
            &copy; {new Date().getFullYear()} SONIEM. Todos los derechos reservados.
          </p>
          <p className="text-secondary-500 text-xs">
            Autorizado por Indecopi - Res. Nº 0054-2011/DDA-INDECOPI
          </p>
        </div>
      </div>

      {/* Background brand text */}
      <div className="absolute bottom-0 left-0 right-0 pointer-events-none select-none overflow-hidden">
        <p className="font-heading text-secondary-800/10 text-[15vw] md:text-[12vw] font-black leading-none whitespace-nowrap">
          SONIEM
        </p>
      </div>
    </footer>
  );
}